import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Trash2, Upload, RotateCcw, Image as ImageIcon, Quote, ShieldCheck } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import api, { API } from "@/lib/api";

const inputCls =
  "bg-midnight border-[#1E293B] text-[#F4F6F8] placeholder:text-[#94A3B8]/50 focus-visible:ring-gold/60 h-12 rounded-sm";

const authHeaders = (token) => ({ headers: { Authorization: `Bearer ${token}` } });

export const TestimonialsManager = ({ token }) => {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ quote: "", name: "", org: "" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/content/testimonials").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/testimonials", form, authHeaders(token));
      toast.success("Testimonial published to the homepage");
      setForm({ quote: "", name: "", org: "" });
      load();
    } catch {
      toast.error("Failed to add testimonial");
    } finally {
      setBusy(false);
    }
  };

  const del = async (id) => {
    try {
      await api.delete(`/admin/testimonials/${id}`, authHeaders(token));
      setItems(items.filter((i) => i.id !== id));
      toast.success("Testimonial removed");
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="testimonials-manager">
      <form onSubmit={add} data-testid="admin-testimonial-form" className="bg-surface border border-[#1E293B] p-6 space-y-4 h-fit">
        <h3 className="font-display font-semibold text-[#F4F6F8]">Add a real client testimonial</h3>
        <p className="text-xs text-[#94A3B8]">The homepage shows placeholders until you add your first real one.</p>
        <div>
          <Label htmlFor="tm-quote" className="text-xs uppercase tracking-wider text-[#94A3B8]">Quote *</Label>
          <Textarea id="tm-quote" data-testid="admin-testimonial-quote" required rows={3} value={form.quote}
            onChange={(e) => setForm({ ...form, quote: e.target.value })} className={`${inputCls} h-auto`}
            placeholder="What the client said..." />
        </div>
        <div>
          <Label htmlFor="tm-name" className="text-xs uppercase tracking-wider text-[#94A3B8]">Name / Role *</Label>
          <Input id="tm-name" data-testid="admin-testimonial-name" required value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls}
            placeholder="e.g. John Mensah, Procurement Director" />
        </div>
        <div>
          <Label htmlFor="tm-org" className="text-xs uppercase tracking-wider text-[#94A3B8]">Company / Region *</Label>
          <Input id="tm-org" data-testid="admin-testimonial-org" required value={form.org}
            onChange={(e) => setForm({ ...form, org: e.target.value })} className={inputCls}
            placeholder="e.g. Accra Energy Ltd, Ghana" />
        </div>
        <button data-testid="admin-testimonial-add" type="submit" disabled={busy}
          className="w-full inline-flex items-center justify-center gap-2 bg-gold text-midnight font-semibold px-6 py-3 rounded-full hover:bg-gold-light transition-colors duration-200 disabled:opacity-60">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />} Publish Testimonial
        </button>
      </form>

      <div className="space-y-3" data-testid="admin-testimonials-list">
        {items.length === 0 && (
          <div className="border border-dashed border-[#1E293B] p-10 text-center text-sm text-[#94A3B8]" data-testid="admin-testimonials-empty">
            No custom testimonials yet — the homepage currently shows the default placeholders.
          </div>
        )}
        {items.map((t) => (
          <div key={t.id} data-testid={`admin-testimonial-${t.id}`} className="bg-surface border border-[#1E293B] p-5 flex gap-4">
            <Quote className="w-4 h-4 text-gold shrink-0 mt-1" />
            <div className="flex-1 min-w-0">
              <p className="text-sm text-[#F4F6F8] leading-relaxed">“{t.quote}”</p>
              <p className="text-xs text-gold mt-2">{t.name}</p>
              <p className="text-xs text-[#94A3B8]">{t.org}</p>
            </div>
            <button data-testid={`admin-testimonial-delete-${t.id}`} onClick={() => del(t.id)}
              className="text-[#94A3B8] hover:text-red-400 transition-colors duration-200 shrink-0" aria-label="Delete testimonial">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export const PartnersManager = ({ token }) => {
  const [items, setItems] = useState([]);
  const [name, setName] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get("/content/partners").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("name", name);
      if (file) fd.append("logo", file);
      await api.post("/admin/partners", fd, authHeaders(token));
      toast.success("Partner added to the homepage");
      setName("");
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Failed to add partner");
    } finally {
      setBusy(false);
    }
  };

  const toggleVerify = async (p) => {
    try {
      await api.patch(`/admin/partners/${p.id}/verify`, { verified: !p.verified }, authHeaders(token));
      setItems(items.map((i) => (i.id === p.id ? { ...i, verified: !p.verified } : i)));
      toast.success(p.verified ? "Verification removed" : "Partner marked as Norex Verified");
    } catch {
      toast.error("Failed to update verification");
    }
  };

  const del = async (id) => {
    try {
      await api.delete(`/admin/partners/${id}`, authHeaders(token));
      setItems(items.filter((i) => i.id !== id));
      toast.success("Partner removed");
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="partners-manager">
      <form onSubmit={add} data-testid="admin-partner-form" className="bg-surface border border-[#1E293B] p-6 space-y-4 h-fit">
        <h3 className="font-display font-semibold text-[#F4F6F8]">Add a client / partner</h3>
        <p className="text-xs text-[#94A3B8]">Upload their logo (PNG, JPG, WebP, max 5MB) — or leave it out to show a styled wordmark.</p>
        <div>
          <Label htmlFor="pt-name" className="text-xs uppercase tracking-wider text-[#94A3B8]">Company Name *</Label>
          <Input id="pt-name" data-testid="admin-partner-name" required value={name}
            onChange={(e) => setName(e.target.value)} className={inputCls} placeholder="e.g. Accra Energy Ltd" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider text-[#94A3B8]">Logo (optional)</Label>
          <input ref={fileRef} id="pt-logo" type="file" accept=".png,.jpg,.jpeg,.webp" className="hidden"
            data-testid="admin-partner-logo-input"
            onChange={(e) => setFile(e.target.files?.[0] || null)} />
          <label htmlFor="pt-logo" data-testid="admin-partner-logo-label"
            className="mt-1 flex items-center gap-3 border border-dashed border-[#1E293B] hover:border-gold/50 transition-colors duration-200 px-4 py-3.5 cursor-pointer text-sm text-[#94A3B8]">
            <Upload className="w-4 h-4 text-gold shrink-0" />
            {file ? <span className="text-[#F4F6F8]">{file.name}</span> : "Upload company logo"}
          </label>
        </div>
        <button data-testid="admin-partner-add" type="submit" disabled={busy}
          className="w-full inline-flex items-center justify-center gap-2 bg-gold text-midnight font-semibold px-6 py-3 rounded-full hover:bg-gold-light transition-colors duration-200 disabled:opacity-60">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />} Add Partner
        </button>
      </form>

      <div className="space-y-3" data-testid="admin-partners-list">
        {items.length === 0 && (
          <div className="border border-dashed border-[#1E293B] p-10 text-center text-sm text-[#94A3B8]" data-testid="admin-partners-empty">
            No partners yet — the homepage currently shows the default placeholder wordmarks.
          </div>
        )}
        {items.map((p) => (
          <div key={p.id} data-testid={`admin-partner-${p.id}`} className="bg-surface border border-[#1E293B] p-5 flex items-center gap-4">
            {p.logo ? (
              <img src={`${API}/public/files/${p.logo.storage_path}`} alt={p.name} className="h-8 max-w-[120px] object-contain" />
            ) : (
              <span className="font-display font-bold tracking-[0.15em] text-sm text-[#94A3B8]">{p.name}</span>
            )}
            <span className="text-xs text-[#94A3B8] ml-2 flex-1 truncate">{p.logo ? p.name : ""}</span>
            <button
              data-testid={`admin-partner-verify-${p.id}`}
              onClick={() => toggleVerify(p)}
              className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider px-2.5 py-1.5 rounded-full border transition-colors duration-200 shrink-0 ${
                p.verified ? "text-gold border-gold/60 bg-gold/10" : "text-[#94A3B8] border-[#1E293B] hover:text-[#F4F6F8]"
              }`}
            >
              <ShieldCheck className="w-3.5 h-3.5" /> {p.verified ? "Verified" : "Verify"}
            </button>
            <button data-testid={`admin-partner-delete-${p.id}`} onClick={() => del(p.id)}
              className="text-[#94A3B8] hover:text-red-400 transition-colors duration-200 shrink-0" aria-label="Delete partner">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

const SLOTS = [
  ["hero", "Hero background"],
  ["about", "About — boardroom"],
  ["contact", "Contact card"],
  ["industry-energy", "Industry — Energy & Oil"],
  ["industry-minerals", "Industry — Minerals & Mining"],
  ["industry-healthcare", "Industry — Healthcare"],
  ["industry-machinery", "Industry — Machinery"],
  ["industry-agriculture", "Industry — Agriculture"],
  ["industry-textiles", "Industry — Textiles"],
  ["industry-construction", "Industry — Construction"],
  ["industry-trade", "Industry — Import & Export"],
];

export const ImagesManager = ({ token }) => {
  const [images, setImages] = useState({});
  const [busyKey, setBusyKey] = useState(null);
  const refs = useRef({});

  const load = () => api.get("/content/images").then(({ data }) => setImages(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const upload = async (key, file) => {
    if (!file) return;
    setBusyKey(key);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/admin/images/${key}`, fd, authHeaders(token));
      toast.success("Photo updated — live on the site now");
      load();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Upload failed");
    } finally {
      setBusyKey(null);
    }
  };

  const reset = async (key) => {
    try {
      await api.delete(`/admin/images/${key}`, authHeaders(token));
      toast.success("Reverted to the stock photo");
      load();
    } catch {
      toast.error("Reset failed");
    }
  };

  return (
    <div data-testid="images-manager">
      <p className="text-sm text-[#94A3B8] mb-6 max-w-2xl">
        Replace any stock photo on the site with your own operations, team, or office photos. Uploads go live
        immediately (PNG, JPG, WebP, max 5MB). You can revert to the original stock photo anytime.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {SLOTS.map(([key, label]) => (
          <div key={key} data-testid={`admin-image-slot-${key}`} className="bg-surface border border-[#1E293B] p-4">
            <div className="h-32 mb-4 border border-[#1E293B] bg-midnight flex items-center justify-center overflow-hidden">
              {images[key] ? (
                <img src={`${API}/public/files/${images[key]}`} alt={label} className="w-full h-full object-cover" />
              ) : (
                <span className="flex items-center gap-2 text-xs text-[#94A3B8]">
                  <ImageIcon className="w-4 h-4" /> Stock photo in use
                </span>
              )}
            </div>
            <p className="text-sm font-semibold text-[#F4F6F8] mb-3">{label}</p>
            <div className="flex gap-2">
              <input ref={(el) => (refs.current[key] = el)} id={`img-${key}`} type="file" accept=".png,.jpg,.jpeg,.webp"
                className="hidden" data-testid={`admin-image-input-${key}`}
                onChange={(e) => { upload(key, e.target.files?.[0]); e.target.value = ""; }} />
              <label htmlFor={`img-${key}`} data-testid={`admin-image-upload-${key}`}
                className="flex-1 inline-flex items-center justify-center gap-2 border border-gold/50 text-gold text-xs font-semibold px-3 py-2 rounded-full cursor-pointer hover:bg-gold hover:text-midnight transition-colors duration-200">
                {busyKey === key ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                {images[key] ? "Replace" : "Upload"}
              </label>
              {images[key] && (
                <button data-testid={`admin-image-reset-${key}`} onClick={() => reset(key)}
                  className="inline-flex items-center gap-1.5 border border-[#1E293B] text-[#94A3B8] text-xs px-3 py-2 rounded-full hover:text-[#F4F6F8] hover:border-white/30 transition-colors duration-200">
                  <RotateCcw className="w-3.5 h-3.5" /> Revert
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
